import { inject, Injectable, signal, Signal } from "@angular/core";
import { Router } from "@angular/router";
import { NavController } from "@ionic/angular";
import { MpSdkService } from "@m481/mp-sdk-ng";
import { BehaviorSubject, from, map, Observable } from "rxjs";

import { AppConstants } from "@mp/app.constants";
import { BaseService } from "@mp/common/base/base.service";
import { UserEntity, UserEntityHelper } from "@mp/common/entities/user.entity";
import { DateToolsService } from "@mp/common/services/date-tools.service";
import { ModalService } from "@mp/common/services/modal.service";
import { CalendarShiftStoreService } from "@mp/common/services/store/calendar-shift-store.service";
import { MissionStoreService } from "@mp/common/services/store/mission-store.service";
import { ShiftStoreService } from "@mp/common/services/store/shift-store.service";
import { TeamStoreService } from "@mp/common/services/store/team-store.service";
import { UserStoreService } from "@mp/common/services/store/user-store.service";
import { StorageService } from "@mp/core/services/storage.service";

@Injectable({
    providedIn: "root"
})
export class AppAuthService extends BaseService {
    public currentUser: Observable<UserEntity | null>;
    private readonly currentUserSubject: BehaviorSubject<UserEntity | null>;

    private readonly router = inject(Router);
    private readonly mpSdkService = inject(MpSdkService);
    private readonly storageService = inject(StorageService);
    private readonly navController = inject(NavController);
    private readonly modalService = inject(ModalService);
    private readonly dateToolsService = inject(DateToolsService);
    private readonly teamStoreService = inject(TeamStoreService);
    private readonly missionStoreService = inject(MissionStoreService);
    private readonly shiftStoreService = inject(ShiftStoreService);
    private readonly calendarShiftStoreService = inject(CalendarShiftStoreService);
    private readonly userStoreService = inject(UserStoreService);

    constructor() {
        super();

        this.currentUserSubject = new BehaviorSubject<UserEntity | null>(null);
        this.currentUser = this.currentUserSubject.asObservable();
        this.hydrateLoggedUser();
        this.log.debug("Auth service initialized");
    }

    public login(username: string, password: string): Observable<UserEntity> {
        return from(this.mpSdkService.login(username, password)).pipe(
            map(userModel => {
                if (userModel) {
                    this.log.debug("Login successfully", userModel);
                    const userEntity = UserEntityHelper.fromModel(userModel);
                    this.saveLoggedUser(userEntity);
                    this.initStores();
                    return userEntity;
                } else {
                    this.log.warn("Unexpected response: user and/or token are invalid", userModel);
                    throw new Error("Unexpected user response");
                }
            })
        );
    }

    public async logout(returnUrl?: string): Promise<void> {
        await this.modalService.closeAllModals();
        this.mpSdkService.logout();
        this.clearUserCache();
        await this.destroyPrivatePagesAndRedirectToPublicHome(returnUrl);
    }

    public isLoggedIn(): boolean {
        return this.mpSdkService.isLoggedIn();
    }

    public isUserAdminOfSelectedTeam(): Signal<boolean> {
        const selectedTeamId = this.teamStoreService.selectedTeamId();
        const user = this.currentUserSubject.getValue();

        if (!selectedTeamId || !user) {
            return signal(false);
        }

        return signal(Array.isArray(user.teamsAdmin) && user.teamsAdmin.includes(selectedTeamId));
    }

    /*
     * Methods to manage user preferences
     */

    public hydrateLoggedUser(): void {
        const persistedUser = this.storageService.getObject<UserEntity>(AppConstants.StorageKeys.USER.DATA);
        if (persistedUser) {
            this.currentUserSubject.next(persistedUser);
        }
    }

    public saveLoggedUser(user: UserEntity): void {
        this.storageService.setObject<UserEntity>(AppConstants.StorageKeys.USER.DATA, user);
        this.currentUserSubject.next(user);
    }

    public removeLoggedUser(): void {
        this.storageService.removeItem(AppConstants.StorageKeys.USER.DATA);
        this.currentUserSubject.next(null);
    }

    /*
     * Private methods
     */

    private clearUserCache(): void {
        this.removeLoggedUser();
        this.storageService.removeItem(AppConstants.StorageKeys.USER.HOME);
        this.dateToolsService.clearSelectedWeek();
        this.clearStores();
    }

    private initStores(): void {
        this.userStoreService.init();
        this.teamStoreService.init();
        this.missionStoreService.init();
        this.shiftStoreService.init();
        this.calendarShiftStoreService.init();
    }

    private clearStores(): void {
        this.userStoreService.reset();
        this.teamStoreService.reset();
        this.missionStoreService.reset();
        this.shiftStoreService.reset();
        this.calendarShiftStoreService.reset();
    }

    private async destroyPrivatePagesAndRedirectToPublicHome(returnUrl?: string): Promise<boolean> {
        const urlTree = returnUrl ? this.router.createUrlTree([AppConstants.DEFAULT_MAIN_PAGE], { queryParams: { returnUrl } }) : this.router.createUrlTree([AppConstants.DEFAULT_MAIN_PAGE]);
        return this.navController.navigateRoot(urlTree, { replaceUrl: true, animated: false });
    }
}
